import { useState } from "react";
import HumanMuscleAnatomy from "../components/HumanMuscleAnatomy";

import plank        from "../assets/photos/Plank.png";
import dog          from "../assets/photos/Dog.png";
import legExtension from "../assets/photos/LegExtension.png";
import crunch       from "../assets/photos/Crunch.png";
import cobra        from "../assets/photos/Cobra.png";
import birdDog      from "../assets/photos/BirdDog.png";
import "../App.css";

const IG = "https://www.instagram.com/zaiqthechiro/";

const EXERCISES = {
  plank:        { src: plank,        name: "Side Plank",    desc: "Targets obliques and core stability.",         link: IG },
  dog:          { src: dog,          name: "Downward Dog",  desc: "Full body stretch — hamstrings & shoulders.",  link: IG },
  legExtension: { src: legExtension, name: "Leg Extension", desc: "Quadriceps strengthening & knee health.",     link: IG },
  crunch:       { src: crunch,       name: "Crunch",        desc: "Rectus abdominis activation.",                link: IG },
  cobra:        { src: cobra,        name: "Cobra Stretch", desc: "Lumbar extension and spine decompression.",   link: IG },
  birdDog:      { src: birdDog,      name: "Bird Dog",      desc: "Core stability and lower back strength.",     link: IG },
};

const BODY_PART_DATA = {
  neck:         { label: "Neck & Cervical",        exercises: ["cobra"],                         tip: "Neck stretches decompress the cervical spine and relieve upper-body tension." },
  shoulders:    { label: "Shoulders & Upper Back", exercises: ["dog"],                           tip: "Shoulder mobility work improves posture and reduces rotator-cuff strain." },
  chest:        { label: "Chest & Pectorals",      exercises: ["dog", "cobra"],                  tip: "Chest opening stretches counteract forward-head posture." },
  arms:         { label: "Arms & Forearms",         exercises: ["dog"],                           tip: "Arm and wrist flexibility helps prevent repetitive-strain injuries." },
  core:         { label: "Core & Abs",             exercises: ["plank", "crunch", "birdDog"],    tip: "Core strength is the foundation of spinal health and injury prevention." },
  "lower-back": { label: "Lower Back",             exercises: ["cobra", "birdDog"],              tip: "Lower back exercises reduce lumbar load and support disc health." },
  hips:         { label: "Hips & Glutes",          exercises: ["dog", "legExtension"],           tip: "Hip mobility relieves SI joint stress and reduces referred back pain." },
  legs:         { label: "Legs & Quads",           exercises: ["legExtension", "dog"],           tip: "Strong quads support the knee joint and reduce patellar tracking issues." },
  knees:        { label: "Knees",                  exercises: ["legExtension"],                  tip: "Quad and calf strengthening protects the knee joint under load." },
  calves:       { label: "Calves & Ankles",        exercises: [],                                tip: "Calf flexibility reduces Achilles strain and improves gait mechanics." },
};

const ExerciseCard = ({ id }) => {
  const ex = EXERCISES[id];
  return (
    <a
      href={ex.link}
      target="_blank"
      rel="noopener noreferrer"
      className="exercise-card flex gap-3 p-3 rounded-xl transition-all duration-200 group"
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.07)",
      }}
      onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(200,150,31,0.25)"}
      onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"}
    >
      <div className="relative w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
        <img src={ex.src} alt={ex.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex items-center justify-center bg-black/45 group-hover:bg-black/25 transition-colors">
          <svg className="w-5 h-5 text-white/80" fill="currentColor" viewBox="0 0 20 20">
            <path d="M6.3 2.841A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
          </svg>
        </div>
      </div>
      <div className="flex flex-col justify-center min-w-0">
        <p className="text-sm font-semibold text-white/90 truncate">{ex.name}</p>
        <p className="text-xs text-white/45 leading-snug mt-0.5 line-clamp-2">{ex.desc}</p>
      </div>
    </a>
  );
};

const ContentPage = () => {
  const [selectedPart, setSelectedPart] = useState(null);
  const [view, setView] = useState("bodymap");
  const [gender, setGender] = useState("male");

  const partData = selectedPart ? BODY_PART_DATA[selectedPart] : null;

  return (
    <div className="flex flex-col flex-1 page-fade-in">
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(200,150,31,0.04) 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-5xl mx-auto w-full px-4 sm:px-6 py-8 flex flex-col gap-6">

        {/* ── Header + View toggle ── */}
        <div className="flex items-center gap-3">
          <span className="section-label mr-auto">Content</span>
          <button
            onClick={() => setView("bodymap")}
            className={`btn-gold ${view === "bodymap" ? "btn-gold-active" : ""}`}
          >
            Body Map
          </button>
          <button
            onClick={() => setView("grid")}
            className={`btn-gold ${view === "grid" ? "btn-gold-active" : ""}`}
          >
            All Exercises
          </button>
        </div>

        <div className="gold-divider" />

        {/* ── Body Map View ── */}
        {view === "bodymap" && (
          <div className="flex flex-col lg:flex-row gap-6">

            {/* Body map */}
            <div className="flex flex-col items-center gap-3 flex-shrink-0">
              <div
                className="glass-card rounded-2xl p-4 flex flex-col items-center gap-3"
                style={{ borderColor: "rgba(255,255,255,0.07)" }}
              >
                <div className="flex items-center gap-1 self-stretch justify-between">
                  <p className="text-[0.65rem] text-white/30 tracking-widest uppercase">
                    Select a muscle group
                  </p>
                  <div className="flex gap-1">
                    {["male", "female"].map((g) => (
                      <button
                        key={g}
                        onClick={() => setGender(g)}
                        className="text-[0.65rem] px-2 py-0.5 rounded-full capitalize transition-all"
                        style={{
                          background: gender === g ? "rgba(200,150,31,0.15)" : "transparent",
                          border: `1px solid ${gender === g ? "rgba(200,150,31,0.4)" : "rgba(255,255,255,0.1)"}`,
                          color: gender === g ? "#d4aa45" : "rgba(255,255,255,0.3)",
                        }}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ width: "340px", height: "380px" }}>
                  <HumanMuscleAnatomy
                    selectedPart={selectedPart}
                    onPartSelect={setSelectedPart}
                    backgroundColor="#0d0d0d"
                    defaultMuscleColor="#2a2a2a"
                    highlightColor="#c8961f"
                    highlightOpacity={0.9}
                    gender={gender}
                  />
                </div>
              </div>

              {/* Legend */}
              <div className="flex gap-5 text-[0.65rem] text-white/30">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#2a2a2a", border: "1px solid #444" }} />
                  Normal
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#3d2e0a", border: "1px solid #c8961f" }} />
                  Selected
                </span>
              </div>
            </div>

            {/* Preview panel */}
            <div className="flex-1 flex flex-col">
              {!selectedPart ? (
                <div
                  className="flex-1 glass-card rounded-2xl flex flex-col items-center justify-center gap-5 p-10 text-center min-h-[300px]"
                  style={{ borderColor: "rgba(255,255,255,0.06)" }}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-xl"
                    style={{ background: "rgba(200,150,31,0.08)", border: "1px solid rgba(200,150,31,0.18)" }}
                  >
                    ✦
                  </div>
                  <div>
                    <p className="text-white/70 font-semibold">Select a body part</p>
                    <p className="text-white/35 text-sm mt-1 max-w-xs">
                      Click any muscle group on the diagram to see related stretches and exercises.
                    </p>
                  </div>
                </div>
              ) : (
                <div
                  className="glass-card rounded-2xl p-5 flex flex-col gap-5 flex-1"
                  style={{ borderColor: "rgba(255,255,255,0.07)" }}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="section-label">Body Region</span>
                      <h3 className="text-xl font-bold text-white mt-1">{partData.label}</h3>
                    </div>
                    <button
                      onClick={() => setSelectedPart(null)}
                      className="text-white/25 hover:text-white/60 transition-colors p-1 mt-1"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Clinical tip */}
                  <p
                    className="text-white/50 text-sm leading-relaxed pl-3 italic border-l-2"
                    style={{ borderColor: "rgba(200,150,31,0.35)" }}
                  >
                    {partData.tip}
                  </p>

                  {/* Exercises */}
                  {partData.exercises.length > 0 ? (
                    <div className="flex flex-col gap-3">
                      <span className="section-label">Exercises & Stretches</span>
                      {partData.exercises.map((id) => (
                        <ExerciseCard key={id} id={id} />
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center flex-1 gap-2 py-6 text-center">
                      <p className="text-white/35 text-sm">More exercises coming soon.</p>
                      <a
                        href={IG}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm transition-colors"
                        style={{ color: "#c8961f" }}
                      >
                        Follow on Instagram →
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        )}

        {/* ── Grid View ── */}
        {view === "grid" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {Object.entries(EXERCISES).map(([id, ex]) => (
              <a
                key={id}
                href={ex.link}
                target="_blank"
                rel="noopener noreferrer"
                className="exercise-card rounded-xl overflow-hidden group"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.07)",
                }}
              >
                <div className="relative aspect-square">
                  <img src={ex.src} alt={ex.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent flex items-end p-2.5">
                    <span className="text-white text-xs font-semibold leading-tight">{ex.name}</span>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: "rgba(200,150,31,0.12)" }}>
                    <svg className="w-9 h-9 drop-shadow-lg" fill="white" viewBox="0 0 20 20">
                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                    </svg>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default ContentPage;
